import {
  useCallback,
  useId,
  useRef,
  useState,
  type ChangeEventHandler,
  type InputHTMLAttributes,
} from 'react';
import { IconArrowsExchange, IconTrash, IconUpload } from '@tabler/icons-react';
import { cx } from '../utils/cx.js';
import { assignFilesToInput } from '../utils/clipboardImage.js';
import { useClipboardImagePaste } from '../utils/useClipboardImagePaste.js';
import { Popover } from './Popover.js';
import { WP_IMAGE_FILE_ACCEPT } from './FilePickerField.js';
import {
  GalleryImagePickerPanel,
  type GalleryImagePickerItem,
} from './GalleryImagePickerPanel.js';
import styles from './ThumbnailImagePicker.module.css';

export type { GalleryImagePickerItem };

export type ThumbnailImagePickerProps = Omit<
  InputHTMLAttributes<HTMLInputElement>,
  'type' | 'children' | 'className' | 'size'
> & {
  className?: string;
  /** Image URL to show in the tile (blob or remote). */
  previewUrl?: string | null;
  chooseLabel?: string;
  changeLabel?: string;
  removeLabel?: string;
  onRemove?: () => void;
  onChange?: ChangeEventHandler<HTMLInputElement>;
  /** Gallery-first: existing images to pick before upload. */
  galleryItems?: GalleryImagePickerItem[];
  /** Selected gallery item id (key). */
  selectedGalleryId?: string | null;
  onGallerySelect?: (id: string) => void;
  /** Called when user uploads via gallery panel (in addition to onChange). */
  onGalleryUpload?: (file: File) => void;
  uploadLabel?: string;
  emptyGalleryLabel?: string;
};

export function ThumbnailImagePicker({
  previewUrl = null,
  chooseLabel = 'Choisir une image',
  changeLabel = "Changer l'image",
  removeLabel = "Retirer l'image",
  onRemove,
  onChange,
  galleryItems = [],
  selectedGalleryId = null,
  onGallerySelect,
  onGalleryUpload,
  uploadLabel = 'Téléverser une nouvelle image',
  emptyGalleryLabel,
  disabled,
  id,
  accept = WP_IMAGE_FILE_ACCEPT,
  multiple,
  name,
  className,
  ...rest
}: ThumbnailImagePickerProps) {
  const autoId = useId();
  const inputId = id ?? `wp-thumb-img-${autoId}`;
  const inputRef = useRef<HTMLInputElement | null>(null);
  const [menuOpen, setMenuOpen] = useState(false);

  const hasImage = typeof previewUrl === 'string' && previewUrl.trim().length > 0;
  const useGalleryPanel =
    galleryItems.length > 0 || Boolean(onGallerySelect) || Boolean(onGalleryUpload);

  const applyPastedFile = useCallback(
    (file: File) => {
      if (disabled) return;
      if (inputRef.current && assignFilesToInput(inputRef.current, [file])) {
        setMenuOpen(false);
        return;
      }
      onGalleryUpload?.(file);
      setMenuOpen(false);
    },
    [disabled, onGalleryUpload],
  );

  const { wrapProps } = useClipboardImagePaste({
    disabled: Boolean(disabled),
    accept,
    extraActive: menuOpen,
    onFile: applyPastedFile,
  });

  const handleFileChange: ChangeEventHandler<HTMLInputElement> = (e) => {
    onChange?.(e);
    const file = e.currentTarget.files?.[0];
    if (file) onGalleryUpload?.(file);
    setMenuOpen(false);
  };

  const handleGalleryUpload = (file: File) => {
    onGalleryUpload?.(file);
    setMenuOpen(false);
  };

  const handleGallerySelect = (itemId: string) => {
    onGallerySelect?.(itemId);
    setMenuOpen(false);
  };

  const handleRemove = () => {
    onRemove?.();
    setMenuOpen(false);
  };

  const input = (
    <input
      {...rest}
      ref={inputRef}
      id={inputId}
      type="file"
      className={styles.visuallyHidden}
      accept={accept}
      multiple={multiple}
      name={name}
      disabled={disabled}
      aria-label={`${chooseLabel}. Coller une image avec Ctrl+V.`}
      onChange={handleFileChange}
    />
  );

  const galleryPanel = useGalleryPanel ? (
    <GalleryImagePickerPanel
      compact
      items={galleryItems}
      selectedId={selectedGalleryId}
      onSelect={handleGallerySelect}
      onUpload={handleGalleryUpload}
      uploadLabel={uploadLabel}
      emptyLabel={emptyGalleryLabel}
      disabled={disabled}
      accept={accept}
    />
  ) : null;

  const legacyMenu = (
    <div className={styles.menu} role="menu">
      <div className={styles.menuPreview} aria-hidden>
        <img src={previewUrl!} alt="" className={styles.menuPreviewThumb} />
        <span className={styles.menuPreviewLabel}>Miniature</span>
      </div>
      <button
        type="button"
        role="menuitem"
        className={styles.menuItem}
        onClick={(e) => {
          e.stopPropagation();
          if (disabled) return;
          inputRef.current?.click();
        }}
      >
        <span className={styles.menuItemIcon} aria-hidden>
          <IconArrowsExchange size={16} stroke={1.5} />
        </span>
        {changeLabel}
      </button>
      {onRemove ? (
        <>
          <div className={styles.menuDivider} role="separator" />
          <button
            type="button"
            role="menuitem"
            className={cx(styles.menuItem, styles.menuItemDanger)}
            onClick={(e) => {
              e.stopPropagation();
              handleRemove();
            }}
          >
            <span className={styles.menuItemIcon} aria-hidden>
              <IconTrash size={16} stroke={1.5} />
            </span>
            {removeLabel}
          </button>
        </>
      ) : null}
    </div>
  );

  const filledMenu = useGalleryPanel ? (
    <div className={styles.menu} role="menu">
      {hasImage ? (
        <div className={styles.menuPreview} aria-hidden>
          <img src={previewUrl!} alt="" className={styles.menuPreviewThumb} />
          <span className={styles.menuPreviewLabel}>Miniature</span>
        </div>
      ) : null}
      {galleryPanel}
      {onRemove ? (
        <>
          <div className={styles.menuDivider} role="separator" />
          <button
            type="button"
            role="menuitem"
            className={cx(styles.menuItem, styles.menuItemDanger)}
            onClick={handleRemove}
          >
            <span className={styles.menuItemIcon} aria-hidden>
              <IconTrash size={16} stroke={1.5} />
            </span>
            {removeLabel}
          </button>
        </>
      ) : null}
    </div>
  ) : (
    legacyMenu
  );

  if (!hasImage) {
    if (useGalleryPanel) {
      return (
        <div className={cx(styles.wrap, className)} {...wrapProps}>
          {input}
          <Popover
            opened={menuOpen}
            onChange={setMenuOpen}
            portal
            align="start"
            portalEstimatedHeight={200}
            portalMinWidth={280}
            portalZIndex="var(--wp-z-popover-over-modal)"
            target={
              <button
                type="button"
                className={styles.emptyTile}
                disabled={disabled}
                aria-label={`${chooseLabel}. Coller une image avec Ctrl+V.`}
                aria-haspopup="dialog"
              >
                <IconUpload size={18} stroke={1.5} aria-hidden />
              </button>
            }
          >
            {filledMenu}
          </Popover>
        </div>
      );
    }

    return (
      <div className={cx(styles.wrap, className)} {...wrapProps}>
        {input}
        <button
          type="button"
          className={styles.emptyTile}
          disabled={disabled}
          aria-label={`${chooseLabel}. Coller une image avec Ctrl+V.`}
          onClick={() => {
            if (disabled) return;
            inputRef.current?.click();
          }}
        >
          <IconUpload size={18} stroke={1.5} aria-hidden />
        </button>
      </div>
    );
  }

  const tile = (
    <button
      type="button"
      className={styles.tile}
      disabled={disabled}
      aria-label={`${changeLabel}. Coller une image avec Ctrl+V.`}
      aria-haspopup="menu"
    >
      <img src={previewUrl!} alt="" className={styles.thumb} />
      <span className={styles.hoverOverlay} aria-hidden>
        <IconArrowsExchange size={16} stroke={1.5} />
      </span>
    </button>
  );

  return (
    <div className={cx(styles.wrap, className)} {...wrapProps}>
      {input}
      <Popover
        opened={menuOpen}
        onChange={setMenuOpen}
        portal
        align="start"
        portalEstimatedHeight={onRemove ? (useGalleryPanel ? 320 : 200) : useGalleryPanel ? 240 : 120}
        portalMinWidth={useGalleryPanel ? 280 : 200}
        portalZIndex="var(--wp-z-popover-over-modal)"
        target={tile}
      >
        {filledMenu}
      </Popover>
    </div>
  );
}
